import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { StoreProvider } from "@/context/StoreContext";
import { FestivalThemeProvider, useFestivalTheme } from "@/context/FestivalThemeContext";
import Navbar, { scrollToSection } from "@/components/Navbar";
import CategoryGrid from "@/components/CategoryGrid";
import EverydayDealRows from "@/components/EverydayDealRows";
import FestDealRows from "@/components/FestDealRows";
import FestHeroCarousel from "@/components/FestHeroCarousel";
import EverydayHeroCarousel from "@/components/EverydayHeroCarousel";
import BulkDeals from "@/components/BulkDeals";
import PrimeStrip from "@/components/PrimeStrip";
import RecentlyViewed from "@/components/RecentlyViewed";
import { BuyAgainSection } from "@/components/Reorder";
import Tracking from "@/components/Tracking";
import WhyUs from "@/components/WhyUs";
import CTA from "@/components/CTA";
import Footer from "@/components/Footer";
import Sparkles from "@/components/Sparkles";
import MobileBottomNav from "@/components/MobileBottomNav";
import RangoliRule from "@/components/RangoliRule";
import FestivalLanterns from "@/components/FestivalLanterns";
import RocketBar from "@/components/RocketBar";
import CartDrawer from "@/components/CartDrawer";
import SupportChat from "@/components/SupportChat";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import WishlistPage from "@/pages/WishlistPage";
import { CareersPage, ContactPage, FaqsPage, HelpCenterPage, NewsroomPage, PrivacyPage, ShippingReturnsPage, TermsPage } from "@/pages/StaticPages";
import CheckoutPage from "@/pages/CheckoutPage";
import OrdersPage from "@/pages/OrdersPage";
import AccountCreditPage from "@/pages/AccountCreditPage";
import AccountPrimePage from "@/pages/AccountPrimePage";
import PrimePage from "@/pages/PrimePage";
import PrimeStorePage from "@/pages/PrimeStorePage";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import AccountInvoicesPage from "@/pages/AccountInvoicesPage";
import CreditApplyPage from "@/pages/CreditApplyPage";
import OrderDetailPage from "@/pages/OrderDetailPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import FestFireworks from "@/components/FestFireworks";
import FestParticles from "@/components/FestParticles";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminCredit from "@/pages/admin/AdminCredit";
import AdminPrime from "@/pages/admin/AdminPrime";
import AdminDelivery from "@/pages/admin/AdminDelivery";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminPayments from "@/pages/admin/AdminPayments";
import AdminSupportLeads from "@/pages/admin/AdminSupportLeads";
import AdminSuppliers from "@/pages/admin/AdminSuppliers";
import AdminPurchases from "@/pages/admin/AdminPurchases";
import AdminReturns from "@/pages/admin/AdminReturns";
import AdminTeam from "@/pages/admin/AdminTeam";
import AdminBusiness from "@/pages/admin/AdminBusiness";
import AdminExpenses from "@/pages/admin/AdminExpenses";
import AdminFulfillment from "@/pages/admin/AdminFulfillment";
import AdminProcurement from "@/pages/admin/AdminProcurement";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminCustomerDetail from "@/pages/admin/AdminCustomerDetail";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminAbandoned from "@/pages/admin/AdminAbandoned";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";
import AdminCampaignDetail from "@/pages/admin/AdminCampaignDetail";
import AdminOffers from "@/pages/admin/AdminOffers";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminThemes from "@/pages/admin/AdminThemes";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import OffersSpotlight from "@/components/OffersSpotlight";

function Landing() {
  const location = useLocation();
  const { content, isFestival, preview, decor } = useFestivalTheme();
  const mainRef = useRef(null);
  useEffect(() => {
    if (location.state?.scrollTo) {
      const t = setTimeout(() => scrollToSection(location.state.scrollTo), 350);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location.state]);
  // Festival scroll-reveal: sections gently fade/slide in (skipped for reduced motion)
  useEffect(() => {
    if (!isFestival || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const els = Array.from(mainRef.current?.children || []).slice(1); // hero animates itself
    els.forEach((el) => el.classList.add("pq-reveal"));
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("in");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.1 });
    els.forEach((el) => io.observe(el));
    return () => {
      io.disconnect();
      els.forEach((el) => el.classList.remove("pq-reveal", "in"));
    };
  }, [isFestival]);
  return (
    <main ref={mainRef}>
      <OffersSpotlight />
      {(preview || (isFestival && content.promo_text)) && (
        <div className="pt-[72px]">
          {preview && (
            <div className="bg-amber-500 px-4 py-2 text-center text-xs font-bold uppercase tracking-[0.18em] text-black" data-testid="theme-preview-ribbon">
              Theme preview — not live
            </div>
          )}
          {isFestival && content.promo_text && (
            <div className="border-b border-line bg-accent-soft px-4 py-2.5 text-center" data-testid="festival-promo-ribbon">
              <p className="text-xs font-semibold tracking-wide text-1 sm:text-sm">{content.promo_text}</p>
            </div>
          )}
        </div>
      )}
      {isFestival ? <FestHeroCarousel /> : <EverydayHeroCarousel />}
      {isFestival && <FestDealRows />}
      {isFestival && decor.rangoli_dividers && <RangoliRule />}
      <CategoryGrid />
      {!isFestival && <EverydayDealRows />}
      <BulkDeals />
      <PrimeStrip />
      {isFestival && decor.rangoli_dividers && <RangoliRule />}
      <section className="mx-auto max-w-7xl px-6 lg:px-12" data-testid="home-rails">
        <RecentlyViewed />
        <BuyAgainSection />
      </section>
      <WhyUs />
      <CTA />
    </main>
  );
}

function TrackPage() {
  return (
    <main className="pt-[72px]">
      <Tracking />
    </main>
  );
}

function ScrollManager() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-app">
        <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!["admin", "owner"].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function AppShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  return (
    <div className="App bg-app text-1" data-testid="paqvero-app">
      {!isAdmin && <Sparkles />}
      {!isAdmin && <FestivalLanterns />}
      {!isAdmin && <FestFireworks />}
      {!isAdmin && <FestParticles />}
      {!isAdmin && <RocketBar />}
      <Navbar />
      <ScrollManager />
      <div className="relative z-[1]">
        <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/track" element={<TrackPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/shop/product/:productId" element={<ProductPage />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/checkout" element={<CheckoutPage />} />
        <Route path="/orders" element={<OrdersPage />} />
        <Route path="/account/credit" element={<AccountCreditPage />} />
        <Route path="/account/prime" element={<AccountPrimePage />} />
        <Route path="/prime" element={<PrimePage />} />
        <Route path="/prime-store" element={<PrimeStorePage />} />
        <Route path="/account/business" element={<BusinessProfilePage />} />
        <Route path="/account/invoices" element={<AccountInvoicesPage />} />
        <Route path="/account/credit/apply" element={<CreditApplyPage />} />
        <Route path="/orders/:orderId" element={<OrderDetailPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/help" element={<HelpCenterPage />} />
        <Route path="/shipping-returns" element={<ShippingReturnsPage />} />
        <Route path="/faqs" element={<FaqsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/careers" element={<CareersPage />} />
        <Route path="/newsroom" element={<NewsroomPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="themes" element={<AdminThemes />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="customers" element={<AdminCustomers />} />
          <Route path="customers/:id" element={<AdminCustomerDetail />} />
          <Route path="reviews" element={<AdminReviews />} />
          <Route path="fulfillment" element={<AdminFulfillment />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="abandoned-carts" element={<AdminAbandoned />} />
          <Route path="campaigns" element={<AdminCampaigns />} />
          <Route path="offers" element={<AdminOffers />} />
          <Route path="campaigns/:id" element={<AdminCampaignDetail />} />
          <Route path="inventory" element={<AdminInventory />} />
          <Route path="credit" element={<AdminCredit />} />
          <Route path="prime" element={<AdminPrime />} />
          <Route path="delivery" element={<AdminDelivery />} />
          <Route path="invoices" element={<AdminInvoices />} />
          <Route path="payments" element={<AdminPayments />} />
          <Route path="support-leads" element={<AdminSupportLeads />} />
          <Route path="suppliers" element={<AdminSuppliers />} />
          <Route path="purchases" element={<AdminPurchases />} />
          <Route path="procurement" element={<AdminProcurement />} />
          <Route path="returns" element={<AdminReturns />} />
          <Route path="team" element={<AdminTeam />} />
          <Route path="business" element={<AdminBusiness />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="expenses" element={<AdminExpenses />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        {!isAdmin && <Footer />}
      </div>
      {!isAdmin && <MobileBottomNav />}
      {!isAdmin && <SupportChat />}
      <CartDrawer />
      <Toaster theme="light" position="bottom-right" />
    </div>
  );
}

function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <FestivalThemeProvider>
            <AppShell />
          </FestivalThemeProvider>
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
